import { lazy, Suspense, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { buttonVariants, Spinner } from '@/components/ui/button';
import { IconX } from '@/components/ui/icons';
import { Empty } from '@/components/ui/misc';
import { providerTypeFor } from '@/lib/agent-utils';
import { useAgents, useProviders } from '@/lib/queries';
import { useIsPhone } from '@/lib/use-media';
import { cn, safeStorage } from '@/lib/utils';
import { usePaneActions } from './actions';
import { cellClass, gridClass } from './grid';
import { Pane, type PaneMenuItem } from './Pane';
import { isChatPane, isSidePane, type PaneKey, pathForPane, useWorkspace } from './store';

const RunsList = lazy(() => import('@/routes/runs').then((m) => ({ default: m.RunsList })));
const RunView = lazy(() => import('@/routes/run-detail').then((m) => ({ default: m.RunView })));
const ChatPane = lazy(() => import('./panes/ChatPane').then((m) => ({ default: m.ChatPane })));
const MasterChatPane = lazy(() =>
  import('@/components/master/MasterChatPane').then((m) => ({ default: m.MasterChatPane })),
);

const LAYOUT_KEY = 'af:workspace-layout';
/** "Columns" puts every open conversation next to each other in one row. */
const COLUMNS_CLASS = 'grid-cols-1 lg:grid-flow-col lg:auto-cols-[minmax(320px,1fr)] lg:overflow-x-auto';

const STATIC_TITLES: Record<string, string> = {
  runs: 'runs · all agents',
  master: 'company chat',
};

/** Short label for a side-drawer tab. */
export function sideLabel(k: PaneKey): string {
  if (k === 'runs') return 'runs';
  if (k.startsWith('run:')) return `run ${k.slice(4, 12)}`;
  return k;
}

function PaneBody({ k, focused }: { k: PaneKey; focused: boolean }) {
  if (k === 'runs') return <RunsList />;
  if (k === 'master') return <MasterChatPane paneKey={k} focused={focused} />;
  const id = k.slice(k.indexOf(':') + 1);
  if (k.startsWith('chat:')) return <ChatPane paneKey={k} conversationId={id} focused={focused} />;
  if (k.startsWith('run:')) return <RunView paneKey={k} runId={id} />;
  return <Empty>Unknown pane.</Empty>;
}

/**
 * Center column. Conversations sit in a grid (1 = full width, 2 = side by side, 3–4 = 2×2, …); runs and the
 * org chart open in a side drawer next to the grid. The URL follows the focused pane.
 */
export function Workspace({
  routePane,
  chatsOnly = false,
}: {
  routePane: PaneKey | null;
  chatsOnly?: boolean;
}) {
  const { state, focus, expand } = useWorkspace();
  const { go, newChat, closePane, closePanes } = usePaneActions();
  const agents = useAgents();
  const providers = useProviders();
  const phone = useIsPhone();
  const [layout, setLayoutState] = useState<'grid' | 'columns'>(() =>
    safeStorage.get<string>(LAYOUT_KEY) === 'columns' ? 'columns' : 'grid',
  );
  const setLayout = (l: 'grid' | 'columns') => {
    setLayoutState(l);
    safeStorage.set(LAYOUT_KEY, l);
  };

  const solo =
    routePane === 'master'
      ? 'master'
      : state.expanded && state.panes.includes(state.expanded)
        ? state.expanded
        : null;
  // A phone shows one conversation at a time (the routed one, else the focused one, else the latest).
  const allChats = solo ? [] : state.panes.filter(isChatPane);
  const phoneChat =
    routePane && allChats.includes(routePane)
      ? routePane
      : state.focused && allChats.includes(state.focused)
        ? state.focused
        : allChats[allChats.length - 1];
  const chats = phone && phoneChat ? [phoneChat] : allChats;
  // On the chats page the conversation list takes the side: runs and the org chart stay in their own views.
  const sides = solo || chatsOnly ? [] : state.panes.filter(isSidePane);
  // The drawer shows the focused side pane, else the most recently opened one.
  const sideKey = state.focused && sides.includes(state.focused) ? state.focused : sides[sides.length - 1];
  const empty = !solo && chats.length === 0 && !sideKey;

  // Navigating to a pane route (sidebar, launcher) opens it: bring it into view when the layout stacks.
  useEffect(() => {
    if (!routePane || typeof document === 'undefined') return;
    const id = requestAnimationFrame(() => {
      for (const el of document.querySelectorAll<HTMLElement>('[data-pane]'))
        if (el.dataset.pane === routePane) el.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [routePane]);

  if (empty) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-border-strong">
        <Empty icon=">">No panes open.</Empty>
        <div className="flex gap-2">
          <button type="button" className={buttonVariants('primary')} onClick={() => go('/master')}>
            Company chat
          </button>
          <button type="button" className={buttonVariants('default')} onClick={() => newChat()}>
            Agent chat
          </button>
          <button type="button" className={buttonVariants('default')} onClick={() => go('/runs')}>
            Runs
          </button>
        </div>
      </div>
    );
  }

  const renderPane = (k: PaneKey, className: string, opts?: { addChat?: boolean }) => {
    const meta = state.meta[k];
    const agent = agents.data?.find((a) => a.id === meta?.agentId);
    const focused = state.focused === k;
    const id = k.slice(k.indexOf(':') + 1);
    const menu: PaneMenuItem[] = [];
    if (agent) menu.push({ label: `Open ${agent.name}`, onSelect: () => go(`/agents/${agent.id}`) });
    if (k.includes(':'))
      menu.push({
        label: 'Copy id',
        onSelect: () => void navigator.clipboard?.writeText(id).then(() => toast.success('Copied')),
      });
    if (state.panes.length > 1)
      menu.push({
        label: 'Close other panes',
        onSelect: () => {
          for (const p of state.panes) if (p !== k) closePane(p);
          go(pathForPane(k));
        },
      });
    return (
      <Pane
        key={k}
        paneId={k}
        title={meta?.title ?? STATIC_TITLES[k] ?? k}
        status={meta?.status ?? (k.includes(':') ? 'idle' : 'active')}
        provider={agent ? providerTypeFor(agent, providers.data) : undefined}
        focused={focused}
        expanded={state.expanded === k}
        onFocus={() => {
          // Only a focus change moves the URL: a pane that is already focused (e.g. its composer
          // grabbing focus on mount) must never navigate away from a route that was just opened.
          if (focused) return;
          focus(k);
          if (k !== routePane) go(pathForPane(k));
        }}
        onExpand={() => expand(state.expanded === k ? null : k)}
        onAdd={opts?.addChat ? () => newChat() : undefined}
        onClose={() => closePane(k)}
        menu={menu}
        className={className}
      >
        <Suspense
          fallback={
            <div className="p-3">
              <Spinner />
            </div>
          }
        >
          <PaneBody k={k} focused={focused} />
        </Suspense>
      </Pane>
    );
  };

  // Bulk actions once more than one pane is open (close all, only the run/side views, layout).
  const runCount = state.panes.filter(isSidePane).length;
  const toolbar =
    !phone && state.panes.length > 1 ? (
      <div
        role="toolbar"
        aria-label="Open panes"
        className="flex shrink-0 items-center gap-1 text-xs text-muted"
      >
        <span className="mr-auto tabular-nums">{state.panes.length} open</span>
        {chats.length > 1 ? (
          <button
            type="button"
            className={buttonVariants('ghost', 'sm')}
            aria-pressed={layout === 'columns'}
            onClick={() => setLayout(layout === 'columns' ? 'grid' : 'columns')}
          >
            {layout === 'columns' ? 'Grid' : 'Columns'}
          </button>
        ) : null}
        {runCount > 0 && runCount < state.panes.length ? (
          <button
            type="button"
            className={buttonVariants('ghost', 'sm')}
            onClick={() => closePanes(isSidePane)}
          >
            Close run views
          </button>
        ) : null}
        <button
          type="button"
          className={buttonVariants('ghost', 'sm')}
          onClick={() => closePanes(() => true)}
        >
          <IconX size={12} /> Close all
        </button>
      </div>
    ) : null;

  if (solo)
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {renderPane(solo, 'min-h-0 flex-1', { addChat: true })}
      </div>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      {toolbar}
      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        {chats.length > 0 ? (
          <div
            className={cn(
              'grid min-h-0 min-w-0 flex-1 auto-rows-[minmax(340px,1fr)] gap-3 max-lg:auto-rows-[minmax(0,1fr)] lg:overflow-y-auto',
              layout === 'columns' ? COLUMNS_CLASS : gridClass(chats.length),
            )}
          >
            {chats.map((k, i) =>
              renderPane(k, cn('min-h-0', layout === 'columns' ? '' : cellClass(i, chats.length)), {
                addChat: true,
              }),
            )}
          </div>
        ) : null}
        {sideKey ? (
          <aside
            aria-label="Details"
            className={cn(
              'flex min-h-[320px] min-w-0 flex-col gap-2',
              chats.length > 0 ? 'lg:w-[42%] lg:max-w-[640px] lg:min-w-[360px] lg:shrink-0' : 'flex-1',
            )}
          >
            {sides.length > 1 ? (
              <div role="tablist" aria-label="Open views" className="flex flex-wrap gap-1">
                {sides.map((k) => (
                  <span
                    key={k}
                    className={cn(
                      'inline-flex h-6 items-center gap-1 rounded-md border pr-1 pl-2.5 font-mono text-2xs',
                      k === sideKey ? 'border-accent text-fg' : 'border-border text-muted',
                    )}
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={k === sideKey}
                      onClick={() => {
                        focus(k);
                        go(pathForPane(k));
                      }}
                    >
                      {sideLabel(k)}
                    </button>
                    <button
                      type="button"
                      aria-label={`Close ${sideLabel(k)}`}
                      className="rounded-sm p-0.5 hover:bg-hover"
                      onClick={() => closePane(k)}
                    >
                      <IconX size={10} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            {renderPane(sideKey, 'min-h-0 flex-1')}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
